import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ru from 'date-fns/locale/ru';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
} from 'chart.js';
import Spinner from './Spinner';
import styles from './Analytics.module.css';
import workoutIconBlack from '../assets/done-black.svg';
import workoutIconWhite from '../assets/done-light.svg';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip);

// Customize Russian locale with nominative month names
const customRuLocale = {
  ...ru,
  localize: {
    ...ru.localize,
    month: (n) => [
      'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
      'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'
    ][n],
  },
};

const Analytics = ({ workoutData, darkMode, loading }) => {
  const [workoutTimeFrame, setWorkoutTimeFrame] = useState('month');
  const [workoutSelectedWeek, setWorkoutSelectedWeek] = useState(new Date());
  const [workoutSelectedMonth, setWorkoutSelectedMonth] = useState(new Date());
  const [workoutSelectedYear, setWorkoutSelectedYear] = useState(new Date());
  
  const [bodyWeightTimeFrame, setBodyWeightTimeFrame] = useState('month');
  const [bodyWeightSelectedMonth, setBodyWeightSelectedMonth] = useState(new Date());
  const [bodyWeightSelectedYear, setBodyWeightSelectedYear] = useState(new Date());
  
  const [exerciseTimeFrame, setExerciseTimeFrame] = useState('month');
  const [exerciseSelectedMonth, setExerciseSelectedMonth] = useState(new Date());
  const [exerciseSelectedYear, setExerciseSelectedYear] = useState(new Date());
  
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [tappedDataByChart, setTappedDataByChart] = useState({
    workout: null,
    bodyWeight: null,
    exercise: null,
  });
  const navigate = useNavigate();

  const totalWorkouts = useMemo(() => {
    return Object.values(workoutData || {}).reduce((sum, workouts) => sum + (Array.isArray(workouts) ? workouts.length : 0), 0);
  }, [workoutData]);

  const hasBodyWeightData = useMemo(() => {
    return Object.values(workoutData || {}).some(workouts =>
      Array.isArray(workouts) && workouts.some(workout => workout.bodyWeight !== null && workout.bodyWeight !== '')
    );
  }, [workoutData]);

  const getWorkoutDateRange = useMemo(() => {
    if (workoutTimeFrame === 'week') {
      const start = new Date(workoutSelectedWeek);
      start.setDate(start.getDate() - start.getDay() + 1);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return { start, end };
    } else if (workoutTimeFrame === 'month') {
      const start = new Date(workoutSelectedMonth.getFullYear(), workoutSelectedMonth.getMonth(), 1);
      const end = new Date(workoutSelectedMonth.getFullYear(), workoutSelectedMonth.getMonth() + 1, 0);
      return { start, end };
    } else if (workoutTimeFrame === 'year') {
      const start = new Date(workoutSelectedYear.getFullYear(), 0, 1);
      const end = new Date(workoutSelectedYear.getFullYear(), 11, 31);
      return { start, end };
    }
    return { start: new Date(), end: new Date() };
  }, [workoutTimeFrame, workoutSelectedWeek, workoutSelectedMonth, workoutSelectedYear]);

  const getBodyWeightDateRange = useMemo(() => {
    if (bodyWeightTimeFrame === 'month') {
      const start = new Date(bodyWeightSelectedMonth.getFullYear(), bodyWeightSelectedMonth.getMonth(), 1);
      const end = new Date(bodyWeightSelectedMonth.getFullYear(), bodyWeightSelectedMonth.getMonth() + 1, 0);
      return { start, end };
    } else if (bodyWeightTimeFrame === 'year') {
      const start = new Date(bodyWeightSelectedYear.getFullYear(), 0, 1);
      const end = new Date(bodyWeightSelectedYear.getFullYear(), 11, 31);
      return { start, end };
    }
    return { start: new Date(), end: new Date() };
  }, [bodyWeightTimeFrame, bodyWeightSelectedMonth, bodyWeightSelectedYear]);

  const getExerciseDateRange = useMemo(() => {
    if (exerciseTimeFrame === 'month') {
      const start = new Date(exerciseSelectedMonth.getFullYear(), exerciseSelectedMonth.getMonth(), 1);
      const end = new Date(exerciseSelectedMonth.getFullYear(), exerciseSelectedMonth.getMonth() + 1, 0);
      return { start, end };
    } else if (exerciseTimeFrame === 'year') {
      const start = new Date(exerciseSelectedYear.getFullYear(), 0, 1);
      const end = new Date(exerciseSelectedYear.getFullYear(), 11, 31);
      return { start, end };
    }
    return { start: new Date(), end: new Date() };
  }, [exerciseTimeFrame, exerciseSelectedMonth, exerciseSelectedYear]);

  const getWorkoutCountData = useCallback(() => {
    const { start, end } = getWorkoutDateRange;
    const labels = [];
    const data = [];
    const pointRadii = [];

    if (workoutTimeFrame === 'week') {
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dateKey = d.toDateString();
        labels.push(d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }));
        const workoutCount = (workoutData[dateKey] || []).length;
        data.push(workoutCount);
        pointRadii.push(workoutCount > 0 ? 5 : 0);
      }
    } else if (workoutTimeFrame === 'month') {
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dateKey = d.toDateString();
        labels.push(d.toLocaleDateString('ru-RU', { day: 'numeric' }));
        const workoutCount = (workoutData[dateKey] || []).length;
        data.push(workoutCount);
        pointRadii.push(workoutCount > 0 ? 5 : 0);
      }
    } else if (workoutTimeFrame === 'year') {
      for (let month = 0; month < 12; month++) {
        const monthStart = new Date(start.getFullYear(), month, 1);
        const monthEnd = new Date(start.getFullYear(), month + 1, 0);
        labels.push(monthStart.toLocaleDateString('ru-RU', { month: 'short' }));
        const workoutsInMonth = Object.keys(workoutData || {})
          .filter((date) => {
            const workoutDate = new Date(date);
            return workoutDate >= monthStart && workoutDate <= monthEnd;
          })
          .reduce((sum, date) => sum + (workoutData[date] || []).length, 0);
        data.push(workoutsInMonth);
        pointRadii.push(workoutsInMonth > 0 ? 5 : 0);
      }
    }

    return {
      labels,
      datasets: data.length > 0 ? [{
        label: 'Тренировки',
        data,
        borderColor: darkMode ? '#ffffff' : '#000000',
        backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
        pointBackgroundColor: darkMode ? '#ffffff' : '#000000',
        pointBorderColor: darkMode ? '#ffffff' : '#000000',
        fill: true,
        tension: 0.4,
        pointRadius: pointRadii,
        pointHoverRadius: pointRadii.map(r => r > 0 ? 8 : 0),
      }] : [],
    };
  }, [workoutData, workoutTimeFrame, getWorkoutDateRange, darkMode]);

  const getBodyWeightData = useCallback(() => {
    const { start, end } = getBodyWeightDateRange;
    const history = [];

    Object.entries(workoutData || {}).forEach(([date, workouts]) => {
      if (Array.isArray(workouts)) {
        workouts.forEach((workout) => {
          if (workout.bodyWeight !== null && workout.bodyWeight !== '') {
            history.push({
              date: new Date(date),
              weight: parseFloat(workout.bodyWeight) || 0,
            });
          }
        });
      }
    });

    const filteredHistory = history.filter((entry) => entry.date >= start && entry.date <= end);
    if (filteredHistory.length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const aggregatedData = {};
    if (bodyWeightTimeFrame === 'month') {
      filteredHistory.forEach((entry) => {
        const dateKey = entry.date.toDateString();
        if (!aggregatedData[dateKey] || entry.date > aggregatedData[dateKey].date) {
          aggregatedData[dateKey] = { weight: entry.weight, date: entry.date };
        }
      });
    } else if (bodyWeightTimeFrame === 'year') {
      filteredHistory.forEach((entry) => {
        const monthKey = `${entry.date.getFullYear()}-${entry.date.getMonth()}`;
        if (!aggregatedData[monthKey] || entry.date > aggregatedData[monthKey].date) {
          aggregatedData[monthKey] = { weight: entry.weight, date: entry.date };
        }
      });
    }

    const labels = [];
    const weightData = [];
    const pointRadii = [];

    if (bodyWeightTimeFrame === 'month') {
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dateKey = d.toDateString();
        labels.push(d.toLocaleDateString('ru-RU', { day: 'numeric' }));
        const entry = aggregatedData[dateKey] || { weight: 0 };
        weightData.push(entry.weight);
        pointRadii.push(entry.weight > 0 ? 5 : 0);
      }
    } else if (bodyWeightTimeFrame === 'year') {
      for (let month = 0; month < 12; month++) {
        const monthStart = new Date(start.getFullYear(), month, 1);
        const monthKey = `${monthStart.getFullYear()}-${monthStart.getMonth()}`;
        labels.push(monthStart.toLocaleDateString('ru-RU', { month: 'short' }));
        const entry = aggregatedData[monthKey] || { weight: 0 };
        weightData.push(entry.weight);
        pointRadii.push(entry.weight > 0 ? 5 : 0);
      }
    }

    return {
      labels,
      datasets: weightData.length > 0 ? [{
        label: 'Вес тела (кг)',
        data: weightData,
        borderColor: darkMode ? '#ffffff' : '#000000',
        backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
        pointBackgroundColor: darkMode ? '#ffffff' : '#000000',
        pointBorderColor: darkMode ? '#ffffff' : '#000000',
        fill: true,
        tension: 0.4,
        pointRadius: pointRadii,
        pointHoverRadius: pointRadii.map(r => r > 0 ? 8 : 0),
      }] : [],
    };
  }, [workoutData, bodyWeightTimeFrame, getBodyWeightDateRange, darkMode]);

  const availableExercises = useMemo(() => {
    const exercises = new Set();
    Object.values(workoutData || {}).forEach((workouts) => {
      if (Array.isArray(workouts)) {
        workouts.forEach((workout) => {
          if (Array.isArray(workout.exercises)) {
            workout.exercises.forEach((exercise) => {
              const exerciseName = exercise.exercise || exercise.name;
              if (exerciseName) exercises.add(exerciseName);
            });
          }
        });
      }
    });
    return Array.from(exercises).sort();
  }, [workoutData]);

  const getExerciseStats = useCallback(() => {
    if (!exerciseSearch) {
      return {
        labels: [],
        datasets: [],
        allSets: {},
      };
    }

    const history = [];
    const allSets = {};
    Object.entries(workoutData || {}).forEach(([date, workouts]) => {
      if (Array.isArray(workouts)) {
        workouts.forEach((workout) => {
          if (Array.isArray(workout.exercises)) {
            workout.exercises.forEach((exercise) => {
              const exerciseName = exercise.exercise || exercise.name;
              if (exerciseName === exerciseSearch && Array.isArray(exercise.sets)) {
                if (!allSets[date]) allSets[date] = [];
                exercise.sets.forEach((set, setIndex) => {
                  if (set && typeof set === 'object') {
                    const weightType = set.weightType || 'Вес';
                    allSets[date].push({
                      reps: parseInt(set.reps) || 0,
                      weight: parseFloat(set.weight) || 0,
                      setIndex,
                      weightType,
                    });
                    history.push({
                      date: new Date(date),
                      reps: parseInt(set.reps) || 0,
                      weight: parseFloat(set.weight) || 0,
                      weightType,
                      setIndex,
                    });
                  }
                });
              }
            });
          }
        });
      }
    });

    const { start, end } = getExerciseDateRange;
    const filteredHistory = history.filter((entry) => entry.date >= start && entry.date <= end);

    if (filteredHistory.length === 0) {
      return {
        labels: [],
        datasets: [],
        allSets,
      };
    }

    const weightTypes = ['Вес', 'Доп. вес', 'Соб. вес', 'Гриф', 'Резинки'];
    const availableWeightTypes = [...new Set(filteredHistory.map(entry => entry.weightType))];
    const selectedWeightType = availableWeightTypes.includes('Вес') ? 'Вес' : availableWeightTypes[0] || 'Вес';

    const aggregatedData = {};
    if (exerciseTimeFrame === 'month') {
      filteredHistory
        .filter((entry) => entry.weightType === selectedWeightType)
        .forEach((entry) => {
          const dateKey = entry.date.toDateString();
          if (!aggregatedData[dateKey]) {
            aggregatedData[dateKey] = { maxWeight: -Infinity, repsAtMaxWeight: 0 };
          }
          if (entry.weight > aggregatedData[dateKey].maxWeight) {
            aggregatedData[dateKey] = { maxWeight: entry.weight, repsAtMaxWeight: entry.reps };
          }
        });
    } else if (exerciseTimeFrame === 'year') {
      filteredHistory
        .filter((entry) => entry.weightType === selectedWeightType)
        .forEach((entry) => {
          const monthKey = `${entry.date.getFullYear()}-${entry.date.getMonth()}`;
          if (!aggregatedData[monthKey]) {
            aggregatedData[monthKey] = { maxWeight: -Infinity, repsAtMaxWeight: 0 };
          }
          if (entry.weight > aggregatedData[monthKey].maxWeight) {
            aggregatedData[monthKey] = { maxWeight: entry.weight, repsAtMaxWeight: 0 };
          }
        });
    }

    const labels = [];
    const datasets = [
      {
        label: `Повторения`,
        data: [],
        pointRadius: [],
        pointHoverRadius: [],
        borderColor: darkMode ? '#ffffff' : '#000000',
        backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
        pointBackgroundColor: darkMode ? '#ffffff' : '#000000',
        pointBorderColor: darkMode ? '#ffffff' : '#000000',
        fill: true,
        tension: 0.4,
        yAxisID: 'y',
      },
      {
        label: `Вес (кг)`,
        data: [],
        pointRadius: [],
        pointHoverRadius: [],
        borderColor: darkMode ? '#ffffff' : '#000000',
        backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
        pointBackgroundColor: darkMode ? '#ffffff' : '#000000',
        pointBorderColor: darkMode ? '#ffffff' : '#000000',
        fill: true,
        tension: 0.4,
        yAxisID: 'y1',
      },
    ];

    if (exerciseTimeFrame === 'month') {
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dateKey = d.toDateString();
        labels.push(d.toLocaleDateString('ru-RU', { day: 'numeric' }));
        const entry = aggregatedData[dateKey] || { maxWeight: 0, repsAtMaxWeight: 0 };
        datasets[0].data.push(entry.repsAtMaxWeight);
        datasets[0].pointRadius.push(entry.repsAtMaxWeight > 0 ? 5 : 0);
        datasets[0].pointHoverRadius.push(entry.repsAtMaxWeight > 0 ? 8 : 0);
        datasets[1].data.push(entry.maxWeight);
        datasets[1].pointRadius.push(entry.maxWeight > 0 ? 5 : 0);
        datasets[1].pointHoverRadius.push(entry.maxWeight > 0 ? 8 : 0);
      }
    } else if (exerciseTimeFrame === 'year') {
      for (let month = 0; month < 12; month++) {
        const monthStart = new Date(start.getFullYear(), month, 1);
        const monthKey = `${monthStart.getFullYear()}-${monthStart.getMonth()}`;
        labels.push(monthStart.toLocaleDateString('ru-RU', { month: 'short' }));
        const entry = aggregatedData[monthKey] || { maxWeight: 0, repsAtMaxWeight: 0 };
        datasets[0].data.push(entry.repsAtMaxWeight);
        datasets[0].pointRadius.push(entry.repsAtMaxWeight > 0 ? 5 : 0);
        datasets[0].pointHoverRadius.push(entry.repsAtMaxWeight > 0 ? 8 : 0);
        datasets[1].data.push(entry.maxWeight);
        datasets[1].pointRadius.push(entry.maxWeight > 0 ? 5 : 0);
        datasets[1].pointHoverRadius.push(entry.maxWeight > 0 ? 8 : 0);
      }
    }

    return { labels, datasets: datasets[0].data.some(v => v > 0) || datasets[1].data.some(v => v > 0) ? datasets : [], allSets };
  }, [exerciseSearch, workoutData, exerciseTimeFrame, getExerciseDateRange, darkMode]);

  const handleChartClick = useCallback((event, elements, chart, chartType) => {
    if (elements.length > 0) {
      const { datasetIndex, index } = elements[0];
      const dataset = chart.data.datasets[datasetIndex];
      const value = dataset.data[index];
      if (value === 0) return;
      const label = chart.data.labels[index];

      if (chartType === 'exercise') {
        const { allSets } = getExerciseStats();
        let dateKey;
        let date;
        if (exerciseTimeFrame === 'month') {
          date = new Date(getExerciseDateRange.start.getFullYear(), getExerciseDateRange.start.getMonth(), index + 1);
          dateKey = date.toDateString();
        } else {
          date = new Date(getExerciseDateRange.start.getFullYear(), index, 1);
          dateKey = Object.keys(allSets).find(key => {
            const d = new Date(key);
            return d.getFullYear() === date.getFullYear() && d.getMonth() === date.getMonth();
          });
        }
        const setsForDate = allSets[dateKey] || [];

        setTappedDataByChart({
          workout: null,
          bodyWeight: null,
          exercise: {
            date: date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }),
            sets: setsForDate,
          },
        });
      } else {
        setTappedDataByChart({
          workout: null,
          bodyWeight: null,
          exercise: null,
          [chartType]: {
            label: dataset.label,
            value,
            date: label,
            unit: dataset.label.includes('(кг)') ? 'кг' : '',
          },
        });
      }
    } else {
      setTappedDataByChart({
        workout: null,
        bodyWeight: null,
        exercise: null,
      });
    }
  }, [getExerciseStats, exerciseTimeFrame, getExerciseDateRange]);

  const handleCloseTappedData = useCallback((chartType) => {
    setTappedDataByChart((prev) => ({
      ...prev,
      [chartType]: null,
    }));
  }, []);

  const getMaxY = (data, yAxisID) => {
    if (!data || !data.datasets || data.datasets.length === 0) return yAxisID === 'y' ? 15 : 100;
    const values = data.datasets
      .filter(dataset => dataset.yAxisID === yAxisID)
      .flatMap(dataset => dataset.data || []);
    const validValues = values.filter(v => v !== 0);
    if (validValues.length === 0) return yAxisID === 'y' ? 15 : 100;
    const max = Math.max(...validValues);
    if (yAxisID === 'y') {
      return max + 5; // Buffer for repetitions
    } else {
      // For weight, ensure at least 100 kg or 25% above max
      return Math.max(100, max * 1.25);
    }
  };

  const chartOptions = useMemo(() => {
    const exerciseData = getExerciseStats();
    const suggestedMaxReps = getMaxY(exerciseData, 'y');
    const suggestedMaxWeight = getMaxY(exerciseData, 'y1');

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top',
          labels: {
            color: darkMode ? '#ffffff' : '#000000',
            font: { size: 14, weight: '500', family: 'Rubik, sans-serif' },
            padding: 15,
            boxWidth: 20,
            usePointStyle: true,
          },
        },
        tooltip: {
          enabled: true,
          backgroundColor: darkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)',
          titleColor: darkMode ? '#ffffff' : '#000000',
          bodyColor: darkMode ? '#ffffff' : '#000000',
          borderColor: darkMode ? '#ffffff' : '#000000',
          borderWidth: 1,
          mode: 'nearest',
          intersect: true,
          padding: 10,
          callbacks: {
            title: (tooltipItems) => {
              if (tooltipItems.length === 0) return '';
              const index = tooltipItems[0].dataIndex;
              const chart = tooltipItems[0].chart;
              const labels = chart.data.labels;
              const { start } = getExerciseDateRange;
              let date;
              if (exerciseTimeFrame === 'month') {
                date = new Date(start.getFullYear(), start.getMonth(), index + 1);
              } else {
                date = new Date(start.getFullYear(), index, 1);
              }
              return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
            },
            label: (context) => {
              const label = context.dataset.label || '';
              const value = context.parsed.y || 0;
              const index = context.dataIndex;
              const chart = context.chart;
              const reps = chart.data.datasets[0].data[index];
              const weight = chart.data.datasets[1].data[index];
              if (label === 'Повторения') {
                return [`${label}: ${value}`, `Вес: ${weight} кг`];
              }
              return `${label}: ${value} кг`;
            },
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: exerciseTimeFrame === 'year' ? 'Месяц' : 'Дата',
            color: darkMode ? '#ffffff' : '#000000',
            font: { size: 14, weight: '500', family: 'Rubik, sans-serif' },
            padding: { top: 10 },
          },
          ticks: {
            color: darkMode ? '#ffffff' : '#000000',
            maxTicksLimit: 10,
            font: { size: 12, family: 'Rubik, sans-serif' },
          },
          grid: {
            display: false,
            borderColor: darkMode ? '#ffffff' : '#000000',
          },
        },
        y: {
          type: 'linear',
          position: 'left',
          title: {
            display: true,
            text: 'Повторения',
            color: darkMode ? '#ffffff' : '#000000',
            font: { size: 14, weight: '500', family: 'Rubik, sans-serif' },
            padding: { bottom: 10 },
          },
          ticks: {
            color: darkMode ? '#ffffff' : '#000000',
            maxTicksLimit: 6,
            stepSize: 5,
            precision: 0,
            font: { size: 12, family: 'Rubik, sans-serif' },
          },
          grid: {
            color: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
            borderColor: darkMode ? '#ffffff' : '#000000',
          },
          min: 0,
          suggestedMax: suggestedMaxReps,
        },
        y1: {
          type: 'linear',
          position: 'right',
          title: {
            display: true,
            text: 'Вес (кг)',
            color: darkMode ? '#ffffff' : '#000000',
            font: { size: 14, weight: '500', family: 'Rubik, sans-serif' },
            padding: { bottom: 10 },
          },
          ticks: {
            color: darkMode ? '#ffffff' : '#000000',
            maxTicksLimit: 6,
            stepSize: 10,
            precision: 0,
            font: { size: 12, family: 'Rubik, sans-serif' },
          },
          grid: {
            display: false,
            borderColor: darkMode ? '#ffffff' : '#000000',
          },
          min: 0,
          suggestedMax: suggestedMaxWeight,
        },
      },
      interaction: {
        mode: 'nearest',
        intersect: true,
        axis: 'x',
      },
      onClick: (event, elements, chart) => handleChartClick(event, elements, chart, 'exercise'),
    };
  }, [darkMode, exerciseTimeFrame, handleChartClick, getExerciseStats, getExerciseDateRange]);

  const workoutChartOptions = useMemo(() => {
    const workoutData = getWorkoutCountData();
    return {
      ...chartOptions,
      plugins: {
        ...chartOptions.plugins,
        tooltip: {
          ...chartOptions.plugins.tooltip,
          callbacks: {
            title: (tooltipItems) => {
              if (tooltipItems.length === 0) return '';
              const index = tooltipItems[0].dataIndex;
              const { start } = getWorkoutDateRange;
              let date;
              if (workoutTimeFrame === 'week') {
                date = new Date(start);
                date.setDate(start.getDate() + index);
              } else if (workoutTimeFrame === 'month') {
                date = new Date(start.getFullYear(), start.getMonth(), index + 1);
              } else {
                date = new Date(start.getFullYear(), index, 1);
              }
              return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
            },
            label: (context) => {
              const label = context.dataset.label || '';
              const value = context.parsed.y || 0;
              return `${label}: ${value}`;
            },
          },
        },
      },
      scales: {
        ...chartOptions.scales,
        x: {
          title: {
            display: true,
            text: workoutTimeFrame === 'year' ? 'Месяц' : 'Дата',
            color: darkMode ? '#ffffff' : '#000000',
            font: { size: 14, weight: '500', family: 'Rubik, sans-serif' },
            padding: { top: 10 },
          },
          ticks: {
            color: darkMode ? '#ffffff' : '#000000',
            maxTicksLimit: 10,
            font: { size: 12, family: 'Rubik, sans-serif' },
          },
          grid: {
            display: false,
            borderColor: darkMode ? '#ffffff' : '#000000',
          },
        },
        y: {
          type: 'linear',
          position: 'left',
          title: {
            display: true,
            text: 'Количество тренировок',
            color: darkMode ? '#ffffff' : '#000000',
            font: { size: 14, weight: '500', family: 'Rubik, sans-serif' },
            padding: { bottom: 10 },
          },
          ticks: {
            color: darkMode ? '#ffffff' : '#000000',
            maxTicksLimit: 6,
            stepSize: 1,
            precision: 0,
            font: { size: 12, family: 'Rubik, sans-serif' },
          },
          grid: {
            color: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
            borderColor: darkMode ? '#ffffff' : '#000000',
          },
          min: 0,
          suggestedMax: 5,
        },
        y1: { display: false },
      },
      onClick: (event, elements, chart) => handleChartClick(event, elements, chart, 'workout'),
    };
  }, [chartOptions, darkMode, workoutTimeFrame, handleChartClick, getWorkoutCountData, getWorkoutDateRange]);

  const bodyWeightChartOptions = useMemo(() => {
    const bodyWeightData = getBodyWeightData();
    const suggestedMaxWeight = getMaxY(bodyWeightData, 'y');

    return {
      ...chartOptions,
      plugins: {
        ...chartOptions.plugins,
        tooltip: {
          ...chartOptions.plugins.tooltip,
          callbacks: {
            title: (tooltipItems) => {
              if (tooltipItems.length === 0) return '';
              const index = tooltipItems[0].dataIndex;
              const { start } = getBodyWeightDateRange;
              let date;
              if (bodyWeightTimeFrame === 'month') {
                date = new Date(start.getFullYear(), start.getMonth(), index + 1);
              } else {
                date = new Date(start.getFullYear(), index, 1);
              }
              return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
            },
            label: (context) => {
              const label = context.dataset.label || '';
              const value = context.parsed.y || 0;
              return `${label}: ${value} кг`;
            },
          },
        },
      },
      scales: {
        ...chartOptions.scales,
        x: {
          title: {
            display: true,
            text: bodyWeightTimeFrame === 'year' ? 'Месяц' : 'Дата',
            color: darkMode ? '#ffffff' : '#000000',
            font: { size: 14, weight: '500', family: 'Rubik, sans-serif' },
            padding: { top: 10 },
          },
          ticks: {
            color: darkMode ? '#ffffff' : '#000000',
            maxTicksLimit: 10,
            font: { size: 12, family: 'Rubik, sans-serif' },
          },
          grid: {
            display: false,
            borderColor: darkMode ? '#ffffff' : '#000000',
          },
        },
        y: {
          type: 'linear',
          position: 'left',
          title: {
            display: true,
            text: 'Вес тела (кг)',
            color: darkMode ? '#ffffff' : '#000000',
            font: { size: 14, weight: '500', family: 'Rubik, sans-serif' },
            padding: { bottom: 10 },
          },
          ticks: {
            color: darkMode ? '#ffffff' : '#000000',
            maxTicksLimit: 6,
            stepSize: 5,
            precision: 0,
            font: { size: 12, family: 'Rubik, sans-serif' },
          },
          grid: {
            color: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
            borderColor: darkMode ? '#ffffff' : '#000000',
          },
          min: 0,
          suggestedMax: suggestedMaxWeight,
        },
        y1: { display: false },
      },
      onClick: (event, elements, chart) => handleChartClick(event, elements, chart, 'bodyWeight'),
    };
  }, [chartOptions, darkMode, bodyWeightTimeFrame, handleChartClick, getBodyWeightData, getBodyWeightDateRange]);

  const hasWorkouts = totalWorkouts > 0;

  const handleReturnHome = useCallback(() => {
    navigate('/home');
  }, [navigate]);

  if (loading) {
    return <Spinner darkMode={darkMode} />;
  }

  return (
    <div className={`${styles.container} ${darkMode ? styles.dark : ''}`}>
      <h1 className={styles.title}>Аналитика</h1>
      {hasWorkouts ? (
        <>
          <section className={styles.totalWorkouts}>
            <img
              src={darkMode ? workoutIconWhite : workoutIconBlack}
              alt="Иконка тренировок"
              className={styles.workoutIcon}
            />
            <h2 className={styles.totalWorkoutsText}>Всего тренировок</h2>
            <span className={styles.totalWorkoutsCount}>{totalWorkouts}</span>
          </section>

          <section className={styles.section}>
            <h2 className={styles.subtitle}>Количество тренировок</h2>
            <div className={styles.controls}>
              <div className={styles.timeFrameSelector}>
                <button
                  className={workoutTimeFrame === 'week' ? styles.activeButton : styles.button}
                  onClick={() => setWorkoutTimeFrame('week')}
                >
                  Неделя
                </button>
                <button
                  className={workoutTimeFrame === 'month' ? styles.activeButton : styles.button}
                  onClick={() => setWorkoutTimeFrame('month')}
                >
                  Месяц
                </button>
                <button
                  className={workoutTimeFrame === 'year' ? styles.activeButton : styles.button}
                  onClick={() => setWorkoutTimeFrame('year')}
                >
                  Год
                </button>
              </div>
              {workoutTimeFrame === 'week' && (
                <DatePicker
                  selected={workoutSelectedWeek}
                  onChange={(date) => setWorkoutSelectedWeek(date)}
                  dateFormat="d MMMM yyyy"
                  locale={customRuLocale}
                  showWeekNumbers
                  showWeekPicker
                  className={styles.datePicker}
                  placeholderText="Выберите неделю"
                />
              )}
              {workoutTimeFrame === 'month' && (
                <DatePicker
                  selected={workoutSelectedMonth}
                  onChange={(date) => setWorkoutSelectedMonth(date)}
                  dateFormat="MMMM yyyy"
                  locale={customRuLocale}
                  showMonthYearPicker
                  className={styles.datePicker}
                  placeholderText="Выберите месяц"
                />
              )}
              {workoutTimeFrame === 'year' && (
                <DatePicker
                  selected={workoutSelectedYear}
                  onChange={(date) => setWorkoutSelectedYear(date)}
                  dateFormat="yyyy"
                  locale={customRuLocale}
                  showYearPicker
                  className={styles.datePicker}
                  placeholderText="Выберите год"
                />
              )}
            </div>
            <div className={`${styles.chartContainer} ${tappedDataByChart.workout ? styles.activeChart : ''}`}>
              <Line data={getWorkoutCountData()} options={workoutChartOptions} />
              <TransitionGroup>
                {tappedDataByChart.workout && (
                  <CSSTransition timeout={{ enter: 300, exit: 0 }} classNames="notification">
                    <div className={styles.tappedData}>
                      <span>
                        {tappedDataByChart.workout.label}: {tappedDataByChart.workout.value} {tappedDataByChart.workout.unit} ({tappedDataByChart.workout.date})
                      </span>
                      <button
                        className={styles.closeButton}
                        onClick={() => handleCloseTappedData('workout')}
                      >
                        ×
                      </button>
                    </div>
                  </CSSTransition>
                )}
              </TransitionGroup>
            </div>
          </section>

          {hasBodyWeightData && (
            <section className={styles.section}>
              <h2 className={styles.subtitle}>Прогресс веса тела</h2>
              <div className={styles.controls}>
                <div className={styles.timeFrameSelector}>
                  <button
                    className={bodyWeightTimeFrame === 'month' ? styles.activeButton : styles.button}
                    onClick={() => setBodyWeightTimeFrame('month')}
                  >
                    Месяц
                  </button>
                  <button
                    className={bodyWeightTimeFrame === 'year' ? styles.activeButton : styles.button}
                    onClick={() => setBodyWeightTimeFrame('year')}
                  >
                    Год
                  </button>
                </div>
                {bodyWeightTimeFrame === 'month' && (
                  <DatePicker
                    selected={bodyWeightSelectedMonth}
                    onChange={(date) => setBodyWeightSelectedMonth(date)}
                    dateFormat="MMMM yyyy"
                    locale={customRuLocale}
                    showMonthYearPicker
                    className={styles.datePicker}
                    placeholderText="Выберите месяц"
                  />
                )}
                {bodyWeightTimeFrame === 'year' && (
                  <DatePicker
                    selected={bodyWeightSelectedYear}
                    onChange={(date) => setBodyWeightSelectedYear(date)}
                    dateFormat="yyyy"
                    locale={customRuLocale}
                    showYearPicker
                    className={styles.datePicker}
                    placeholderText="Выберите год"
                  />
                )}
              </div>
              <div className={`${styles.chartContainer} ${tappedDataByChart.bodyWeight ? styles.activeChart : ''}`}>
                {getBodyWeightData().labels.length > 0 ? (
                  <Line data={getBodyWeightData()} options={bodyWeightChartOptions} />
                ) : (
                  <p className={styles.noDataMessage}>Нет данных о весе для выбранного периода</p>
                )}
                <TransitionGroup>
                  {tappedDataByChart.bodyWeight && (
                    <CSSTransition timeout={{ enter: 300, exit: 0 }} classNames="notification">
                      <div className={styles.tappedData}>
                        <span>
                          {tappedDataByChart.bodyWeight.label}: {tappedDataByChart.bodyWeight.value} {tappedDataByChart.bodyWeight.unit} ({tappedDataByChart.bodyWeight.date})
                        </span>
                        <button
                          className={styles.closeButton}
                          onClick={() => handleCloseTappedData('bodyWeight')}
                        >
                          ×
                        </button>
                      </div>
                    </CSSTransition>
                  )}
                </TransitionGroup>
              </div>
            </section>
          )}

          <section className={styles.section}>
            <h2 className={styles.subtitle}>Статистика по упражнениям</h2>
            <div className={styles.controls}>
              <div className={styles.timeFrameSelector}>
                <button
                  className={exerciseTimeFrame === 'month' ? styles.activeButton : styles.button}
                  onClick={() => setExerciseTimeFrame('month')}
                >
                  Месяц
                </button>
                <button
                  className={exerciseTimeFrame === 'year' ? styles.activeButton : styles.button}
                  onClick={() => setExerciseTimeFrame('year')}
                >
                  Год
                </button>
              </div>
              {exerciseTimeFrame === 'month' && (
                <DatePicker
                  selected={exerciseSelectedMonth}
                  onChange={(date) => setExerciseSelectedMonth(date)}
                  dateFormat="MMMM yyyy"
                  locale={customRuLocale}
                  showMonthYearPicker
                  className={styles.datePicker}
                  placeholderText="Выберите месяц"
                />
              )}
              {exerciseTimeFrame === 'year' && (
                <DatePicker
                  selected={exerciseSelectedYear}
                  onChange={(date) => setExerciseSelectedYear(date)}
                  dateFormat="yyyy"
                  locale={customRuLocale}
                  showYearPicker
                  className={styles.datePicker}
                  placeholderText="Выберите год"
                />
              )}
            </div>
            <input
              type="text"
              list="exercises"
              value={exerciseSearch}
              onChange={(e) => setExerciseSearch(e.target.value)}
              placeholder="Выберите упражнение (например, Жим лежа)"
              className={styles.searchInput}
            />
            <datalist id="exercises">
              {availableExercises.map((exercise) => (
                <option key={exercise} value={exercise} />
              ))}
            </datalist>
            <TransitionGroup>
              <CSSTransition timeout={300} classNames="fade">
                <div className={`${styles.chartContainer} ${styles.exerciseChart} ${tappedDataByChart.exercise ? styles.activeChart : ''}`}>
                  {exerciseSearch ? (
                    getExerciseStats().labels.length > 0 ? (
                      <Line data={getExerciseStats()} options={chartOptions} />
                    ) : (
                      <p className={styles.noDataMessage}>Нет данных для упражнения "{exerciseSearch}"</p>
                    )
                  ) : (
                    <p className={styles.noDataMessage}>Выберите упражнение для отображения статистики</p>
                  )}
                  <TransitionGroup>
                    {tappedDataByChart.exercise && (
                      <CSSTransition timeout={{ enter: 300, exit: 0 }} classNames="notification">
                        <div className={styles.tappedData}>
                          <div>
                            <div className={styles.dateLabel}>{tappedDataByChart.exercise.date}</div>
                            {tappedDataByChart.exercise.sets.map((set, idx) => (
                              <div key={idx} className={styles.setInfo}>
                                Подход {idx + 1}: {set.reps} повт., {['Вес', 'Доп. вес'].includes(set.weightType) ? `${set.weightType.toLowerCase()} ${set.weight} кг` : (set.weightType || 'вес').toLowerCase()}
                              </div>
                            ))}
                          </div>
                          <button
                            className={styles.closeButton}
                            onClick={() => handleCloseTappedData('exercise')}
                          >
                            ×
                          </button>
                        </div>
                      </CSSTransition>
                    )}
                  </TransitionGroup>
                </div>
              </CSSTransition>
            </TransitionGroup>
          </section>
        </>
      ) : (
        <div className={styles.noWorkouts}>
          <h1 className={styles.noWorkoutsMessage}>
            Чтобы появилась аналитика, создайте свою первую тренировку
          </h1>
          <button
            className={styles.createFirstWorkoutButton}
            onClick={() => navigate('/home')}
          >
            Вернуться на главную
          </button>
        </div>
      )}
    </div>
  );
};

export default Analytics;